import { observable } from '@legendapp/state';
import { syncObservable } from '@legendapp/state/sync';
import { ObservablePersistLocalStorage } from '@legendapp/state/persist-plugins/local-storage';
import { apiConfig } from '../lib/apiConfig';
import { READER_TYPOGRAPHY_CHANGE_EVENT } from '../constants/readerTypography';

/**
 * Font configuration for the reader
 * Stored in localStorage for persistence
 */

// Font family options
export const FONT_OPTIONS = [
  {
    id: 'default',
    name: 'Georgia',
    label: 'Default',
    fontFamily: "Georgia, 'Times New Roman', 'Noto Serif SC', 'STSong', 'Songti SC', 'SimSun', 'PingFang SC', 'Hiragino Sans GB', 'Microsoft YaHei', 'Microsoft JhengHei', serif",
    preview: 'Aa',
    // Chinese body stays Regular; load real heading and emphasis weights too.
    googleFont: 'Noto+Serif+SC:wght@400;600;700',
  },
  {
    id: 'inter',
    name: 'Inter',
    label: 'Inter',
    fontFamily: "'Inter', 'PingFang SC', 'Hiragino Sans GB', 'Microsoft YaHei', 'Microsoft JhengHei', sans-serif",
    preview: 'Aa',
    googleFont: 'Inter:wght@400;500;600;700',
  },
  {
    id: 'lxgw',
    name: 'LXGW WenKai Screen',
    label: '文楷',
    fontFamily: "'LXGW WenKai Screen', serif",
    preview: '文',
    // LXGW WenKai is NOT on Google Fonts - use jsDelivr CDN instead
    // Screen variant is optimized for on-screen reading
    cdnStylesheet: 'https://cdn.jsdelivr.net/npm/lxgw-wenkai-screen-webfont@1/style.css',
  },
  {
    id: 'glowsans',
    name: 'Glow Sans SC Extended',
    label: '荧黑',
    fontFamily: "'Glow Sans SC Extended Book', sans-serif",
    preview: '荧',
    // Glow Sans - loaded via vite-plugin-font (build-time subsetting)
    // Font CSS is injected dynamically when loadFont() is called
    loadFont: () => import('../assets/fonts/GlowSansSC-Extended-Book.otf'),
  },
  {
    id: 'literata',
    name: 'Literata',
    label: 'Literata',
    fontFamily: "'Literata', 'STSong', 'Songti SC', 'SimSun', 'PingFang SC', 'Hiragino Sans GB', 'Microsoft YaHei', 'Microsoft JhengHei', serif",
    preview: 'Aa',
    googleFont: 'Literata:opsz,wght@7..72,400;7..72,500;7..72,600;7..72,700',
  },
  {
    id: 'lora',
    name: 'Lora',
    label: 'Lora',
    fontFamily: "'Lora', 'STSong', 'Songti SC', 'SimSun', 'PingFang SC', 'Hiragino Sans GB', 'Microsoft YaHei', 'Microsoft JhengHei', serif",
    preview: 'Aa',
    googleFont: 'Lora:wght@400;500;600;700',
  },
];

// Unitless line heights give larger text more absolute breathing room.
export const SIZE_OPTIONS = [
  { id: 'small', label: 'S', fontSize: '14px', lineHeight: '1.8' },
  { id: 'default', label: 'Default', fontSize: '16px', lineHeight: '1.7' },
  { id: 'medium', label: 'M', fontSize: '18px', lineHeight: '1.8' },
  { id: 'large', label: 'L', fontSize: '20px', lineHeight: '1.7' },
  { id: 'xlarge', label: 'XL', fontSize: '22px', lineHeight: '1.65' },
];

export const WIDTH_OPTIONS = [
  { id: 'narrow', width: '576px' },
  { id: 'medium', width: '648px' },
  { id: 'wide', width: '720px' },
];

const DEFAULT_FONT = 'default';
const DEFAULT_SIZE = 'default';
const DEFAULT_WIDTH = 'medium';

// Track which fonts have been loaded (runtime only, not persisted)
// This is a module-level variable because Sets don't serialize to JSON properly
const fontsLoaded = new Set();

const notifyTypographyChange = () => {
  window.dispatchEvent(new Event(READER_TYPOGRAPHY_CHANGE_EVENT));
};

// Observable store
export const readerFont$ = observable({
  fontFamily: DEFAULT_FONT,
  fontSize: DEFAULT_SIZE,
  lineWidth: DEFAULT_WIDTH,
});

// Persist to localStorage
syncObservable(readerFont$, {
  persist: {
    name: 'reedle-reader-typography',
    plugin: ObservablePersistLocalStorage,
  },
});

/**
 * Get the current font family config object
 */
export const getCurrentFontConfig = () => {
  const fontId = readerFont$.fontFamily.get();
  return FONT_OPTIONS.find(f => f.id === fontId) || FONT_OPTIONS[0];
};

/**
 * Get the current size config object
 */
export const getCurrentSizeConfig = () => {
  const sizeId = readerFont$.fontSize.get();
  return SIZE_OPTIONS.find(s => s.id === sizeId) || SIZE_OPTIONS.find(s => s.id === DEFAULT_SIZE);
};

export const getCurrentWidthConfig = () => {
  const widthId = readerFont$.lineWidth.get();
  return WIDTH_OPTIONS.find(w => w.id === widthId) || WIDTH_OPTIONS.find(w => w.id === DEFAULT_WIDTH);
};

/**
 * Set font family
 */
export const setFontFamily = (fontId) => {
  const fontConfig = FONT_OPTIONS.find(f => f.id === fontId);
  if (fontConfig) {
    readerFont$.fontFamily.set(fontId);
    loadGoogleFont(fontConfig);
    applyFontStyles();
  }
};

/**
 * Set font size
 */
export const setFontSize = (sizeId) => {
  if (SIZE_OPTIONS.find(s => s.id === sizeId)) {
    readerFont$.fontSize.set(sizeId);
    applyFontStyles();
  }
};

export const setLineWidth = (widthId) => {
  if (WIDTH_OPTIONS.find(w => w.id === widthId)) {
    readerFont$.lineWidth.set(widthId);
    applyFontStyles();
  }
};

/**
 * Reset to defaults
 */
export const resetFontSettings = () => {
  readerFont$.fontFamily.set(DEFAULT_FONT);
  readerFont$.fontSize.set(DEFAULT_SIZE);
  readerFont$.lineWidth.set(DEFAULT_WIDTH);
  loadGoogleFont(getCurrentFontConfig());
  applyFontStyles();
};

/**
 * Load a font dynamically (Google Fonts, CDN stylesheet, or vite-plugin-font)
 */
export const loadGoogleFont = async (fontConfig) => {
  if (fontsLoaded.has(fontConfig.id)) return;

  // Check for vite-plugin-font loader (e.g., Glow Sans)
  // This imports the font and triggers CSS injection automatically
  if (fontConfig.loadFont) {
    try {
      await fontConfig.loadFont();
      fontsLoaded.add(fontConfig.id);
      if (readerFont$.fontFamily.get() === fontConfig.id) notifyTypographyChange();
    } catch (e) {
      console.warn(`Failed to load font ${fontConfig.id}:`, e);
    }
    return;
  }

  let href = null;

  // Check for CDN stylesheet (e.g., LXGW WenKai)
  if (fontConfig.cdnStylesheet) {
    href = fontConfig.cdnStylesheet;
  }
  // Otherwise use Google Fonts
  else if (fontConfig.googleFont) {
    href = `${apiConfig.googleFontsBase}/css2?family=${fontConfig.googleFont}&display=swap`;
  }

  if (!href) return;

  const link = document.createElement('link');
  link.href = href;
  link.rel = 'stylesheet';
  link.addEventListener('load', () => {
    if (readerFont$.fontFamily.get() === fontConfig.id) notifyTypographyChange();
    // Stylesheet completion precedes glyph loading. Remeasure overlays after
    // the browser has finished laying out the newly loaded font as well.
    document.fonts?.ready.then(() => {
      if (readerFont$.fontFamily.get() === fontConfig.id) notifyTypographyChange();
    });
  });
  link.addEventListener('error', () => {
    fontsLoaded.delete(fontConfig.id);
    link.remove();
    console.error('[ReaderTypography] Font stylesheet failed to load', {
      fontId: fontConfig.id,
      href,
    });
  });
  document.head.appendChild(link);

  // Mark as loaded
  fontsLoaded.add(fontConfig.id);
};

/**
 * Apply font styles to CSS variables
 */
export const applyFontStyles = () => {
  const fontConfig = getCurrentFontConfig();
  const sizeConfig = getCurrentSizeConfig();
  const widthConfig = getCurrentWidthConfig();

  document.documentElement.style.setProperty('--font-reader', fontConfig.fontFamily);
  document.documentElement.style.setProperty('--font-reader-size', sizeConfig.fontSize);
  document.documentElement.style.setProperty('--font-reader-line-height', sizeConfig.lineHeight);
  document.documentElement.style.setProperty('--font-reader-column-width', widthConfig.width);
  notifyTypographyChange();
};

/**
 * Initialize font settings on app load
 * Call this once when the app starts
 */
export const initializeFontSettings = () => {
  const fontConfig = getCurrentFontConfig();
  loadGoogleFont(fontConfig);
  applyFontStyles();
};
